import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { Frequency, RRuleSet, ALL_WEEKDAYS, type WeekdayStr } from 'rrule';
import type { FieldError } from 'rc-field-form/lib/interface';
import {
  type ScheduleUpsertModalCV50FormValues,
  type UseScheduleUpsertModalCV50Params,
  type ScheduleUpsertModalCV50ContextValue,
  type SelectedScheduleMap,
  type CV50_FREQUENCY,
  ONCE_FREQUENCY,
} from '../types/MachineDetailsRobotSchedule.types';
import { getSelectedMapById, groupScheduleMapsByMapGroupId, mapScheduleMapsToSelectOptions } from '../utils/map';
import { areStringArraysEqual } from '../utils/cleaningZones';
import {
  parseRRuleString,
  toRruleWeekdaysUtc,
  fromRruleWeekdaysUtc,
  toCV50UntilDate,
  toRRuleSet,
} from '../utils/rruleDateTime';
import {
  selectCreateScheduleData,
  selectScheduleMaps,
  selectScheduleMapsIsLoading,
} from '../../../../state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { DEFAULT_CLEANING_DURATION_MINUTES } from '../utils/constants';
import {
  CleaningSuctionEnum,
  type UpdateScheduleInput,
  CleaningModeEnum,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import type { ScheduleServiceCreateScheduleOptions } from 'app/modules/machine-inventory/ScheduleService';
import { RobotScheduleActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSlice';

export const useScheduleUpsertModalCV50State = ({
  robot,
  onClose,
  isOpen,
  isEventOpen,
  selectedEvent,
  selectedCreateDateTime,
  handleOpenUpdateConfirmModal,
}: UseScheduleUpsertModalCV50Params): ScheduleUpsertModalCV50ContextValue => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  const [form] = Form.useForm<ScheduleUpsertModalCV50FormValues>();
  const [scheduleMap, setScheduleMap] = useState<SelectedScheduleMap | undefined>(undefined);
  const [hasErrorScheduleName, setHasErrorScheduleName] = useState<boolean>(false);
  const [hasErrorScheduleMap, setHasErrorScheduleMap] = useState<boolean>(false);
  const [isEditingScheduleName, setIsEditingScheduleName] = useState<boolean>(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [hasScheduleChanges, setHasScheduleChanges] = useState(false);
  const [scheduleHasChanged, setScheduleHasChanged] = useState<Record<string, boolean>>({});
  const eventDtStartLocalRef = useRef<dayjs.Dayjs | undefined>(undefined);
  const editFormValuesRef = useRef<ScheduleUpsertModalCV50FormValues | null>(null);

  const scheduleMapsData = useSelector(selectScheduleMaps);
  const scheduleMaps = useMemo(() => scheduleMapsData ?? [], [scheduleMapsData]);
  const isScheduleMapsLoading = useSelector(selectScheduleMapsIsLoading);
  const scheduleMapOptions = useMemo(
    () => mapScheduleMapsToSelectOptions(scheduleMaps, i18n.language),
    [scheduleMaps, i18n.language]
  );
  const scheduleMapGroups = useMemo(
    () => groupScheduleMapsByMapGroupId(scheduleMaps, i18n.language),
    [scheduleMaps, i18n.language]
  );

  const createScheduleData = useSelector(selectCreateScheduleData);

  const scheduleTime = Form.useWatch('scheduleTime', form) || dayjs();
  const watchedScheduleName = Form.useWatch('scheduleName', form);
  const scheduleName = watchedScheduleName || selectedEvent?.resource.scheduleEvent.name || '-';
  const isScheduleNameInvalid = !watchedScheduleName?.trim();

  useEffect(() => {
    if (isEventOpen) {
      setIsEditingScheduleName(false);
    }
  }, [isEventOpen]);

  const handleToggleEditingScheduleName = useCallback((): void => {
    setIsEditingScheduleName(prev => !prev);
  }, []);

  const expectedCompletionTime = useMemo(
    () =>
      // TODO: remove hardcode value when Map supports duration
      ({
        finishCleaningTime: dayjs(scheduleTime).add(DEFAULT_CLEANING_DURATION_MINUTES, 'minutes'),
      }),
    [scheduleTime]
  );

  const handleScheduleMapChange = useCallback(
    (selectedScheduleMap: SelectedScheduleMap | undefined): void => {
      setScheduleMap(selectedScheduleMap);
      // Areas are specific to a map; a stale selection from a different map must not carry over (FR-023).
      form.setFieldsValue({ cleaningMode: CleaningModeEnum.WholeMap, cleaningZones: [] });
    },
    [form]
  );

  const handleDropdownVisibleChange = useCallback((visible: boolean): void => {
    setIsDropdownVisible(visible);
  }, []);

  const handleEditScheduleMap = useCallback((): void => {
    setScheduleMap(undefined);
    form.resetFields(['scheduleMap', 'suctionMode']);
    form.setFieldsValue({ cleaningMode: CleaningModeEnum.WholeMap, cleaningZones: [] });
    setIsDropdownVisible(true);

    const baseline = editFormValuesRef.current;
    if (baseline) {
      setScheduleHasChanged(prev => ({ ...prev, scheduleMap: baseline.scheduleMap !== undefined }));
    }
  }, [form]);

  const handleFrequencyChange = useCallback(
    (checked: boolean, tag: CV50_FREQUENCY): void => {
      if (!checked) {
        return;
      }

      form.setFieldsValue({
        scheduleFrequency: tag,
      });

      const baseline = editFormValuesRef.current;
      if (baseline) {
        setScheduleHasChanged(prev => ({ ...prev, scheduleFrequency: baseline.scheduleFrequency !== tag }));
      }
    },
    [form]
  );

  const handleWeekdaysChange = useCallback(
    (checked: boolean, tag: WeekdayStr): void => {
      const weekdays = form.getFieldValue('weekdays') ?? [];
      const nextSelectedTags = checked ? [...weekdays, tag] : weekdays.filter((day: WeekdayStr) => day !== tag);
      form.setFieldsValue({
        weekdays: nextSelectedTags,
      });

      const baseline = editFormValuesRef.current;
      if (baseline) {
        setScheduleHasChanged(prev => ({
          ...prev,
          weekdays: baseline.weekdays.toString() !== nextSelectedTags.toString(),
        }));
      }
    },
    [form]
  );

  const handleValidateFields = useCallback(
    (fields?: string[]): void => {
      const fieldsToValidate = fields ?? ['scheduleName', 'scheduleMap'];
      form
        .validateFields(fieldsToValidate)
        .then(() => {
          if (fieldsToValidate.includes('scheduleName')) {
            setHasErrorScheduleName(false);
          }
          if (fieldsToValidate.includes('scheduleMap')) {
            setHasErrorScheduleMap(false);
          }
        })
        .catch(error => {
          const errorFieldNames: string[] = (error?.errorFields ?? []).map(
            (errorField: FieldError) => errorField.name[0]
          );
          if (errorFieldNames.includes('scheduleName')) {
            setHasErrorScheduleName(true);
          }
          if (errorFieldNames.includes('scheduleMap')) {
            setHasErrorScheduleMap(true);
          }
        });
    },
    [form]
  );

  const handleFormValuesChange = useCallback(
    (changedValues: ScheduleUpsertModalCV50FormValues): void => {
      if (isEventOpen) {
        const baseline = editFormValuesRef.current;

        if (baseline) {
          Object.entries(changedValues).forEach(([key, value]) => {
            if (key === 'scheduleDate') {
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: baseline[key]?.format('YYYY-MM-DD') !== value.format('YYYY-MM-DD'),
              }));
            } else if (key === 'scheduleTime') {
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: baseline[key].format('HH:mm') !== value.format('HH:mm'),
              }));
            } else if (key === 'cleaningZones') {
              const baselineValue = (baseline[key] as string[] | undefined) ?? [];
              const nextValue = (value as string[] | undefined) ?? [];
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: !areStringArraysEqual(baselineValue, nextValue),
              }));
            } else {
              setScheduleHasChanged(prev => ({
                ...prev,
                [key]: baseline[key as keyof ScheduleUpsertModalCV50FormValues] !== value,
              }));
            }
          });
        }
      }
    },
    [isEventOpen]
  );

  const handleCleaningZonesChange = useCallback(
    (nextCleaningZones: string[]): void => {
      form.setFieldValue('cleaningZones', nextCleaningZones);

      if (!isEventOpen) {
        return;
      }

      const baselineZones = editFormValuesRef.current?.cleaningZones ?? [];
      setScheduleHasChanged(prev => ({
        ...prev,
        cleaningZones: !areStringArraysEqual(baselineZones, nextCleaningZones),
      }));
    },
    [form, isEventOpen]
  );

  const handleResetState = useCallback((): void => {
    setHasErrorScheduleName(false);
    setHasErrorScheduleMap(false);
    setScheduleMap(undefined);
    setHasScheduleChanges(false);
    setScheduleHasChanged({});
    editFormValuesRef.current = null;
  }, []);

  const resetFormState = useCallback((): void => {
    form.resetFields();
    handleResetState();
  }, [form, handleResetState]);

  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleSaveChanges = useCallback((): void => {
    form
      .validateFields()
      .then(() => {
        setHasErrorScheduleName(false);
        setHasErrorScheduleMap(false);

        handleOpenUpdateConfirmModal();
      })
      .catch(error => {
        const errorFieldNames: string[] = (error?.errorFields ?? []).map(
          (errorField: FieldError) => errorField.name[0]
        );
        setHasErrorScheduleName(errorFieldNames.includes('scheduleName'));
        setHasErrorScheduleMap(errorFieldNames.includes('scheduleMap'));
      });
  }, [form, handleOpenUpdateConfirmModal]);

  const buildRRuleSet = useCallback(
    (values: ScheduleUpsertModalCV50FormValues): RRuleSet => {
      const occurrenceStart =
        isEventOpen && eventDtStartLocalRef.current ? eventDtStartLocalRef.current : values.scheduleDate;

      const dtStart = occurrenceStart
        .hour(values.scheduleTime.hour())
        .minute(values.scheduleTime.minute())
        .second(0)
        .millisecond(0);
      const untilDate = occurrenceStart.add(6, 'day').toDate();

      // BYDAY must follow UTC because DTSTART is serialized with Z
      const localWeekdays = values.weekdays;
      const byweekday = localWeekdays?.length ? toRruleWeekdaysUtc(localWeekdays, dtStart) : undefined;
      const until = values.scheduleFrequency === ONCE_FREQUENCY ? toCV50UntilDate({ until: untilDate }) : undefined;

      return toRRuleSet({
        freq: Frequency.WEEKLY,
        dtstart: dtStart.utc().toDate(),
        byweekday,
        until,
      });
    },
    [isEventOpen]
  );

  const handleSubmit = useCallback(
    (values: ScheduleUpsertModalCV50FormValues): void => {
      const mapId = scheduleMap?.map.id ?? values.scheduleMap ?? undefined;
      const suctionMode = values.suctionMode ?? CleaningSuctionEnum.Normal;
      const cleaningZones = values.cleaningMode === CleaningModeEnum.Area ? values.cleaningZones ?? [] : [];

      if (!isEventOpen) {
        const createInput: ScheduleServiceCreateScheduleOptions = {
          materialNumber: robot.materialNumber,
          serialNumber: robot.serialNumber,
          mapId,
          rrule: buildRRuleSet(values).toString(),
          name: scheduleName,
          suction: suctionMode,
          cleaningZones,
        };

        dispatch(RobotScheduleActions.createScheduleRequest(createInput));
        return;
      }

      const occurrence = selectedEvent?.resource.scheduleEvent.occurrence;
      const scheduleId = occurrence?.scheduleId;

      if (!scheduleId || !occurrence) {
        return;
      }

      const hasRruleChanges = (['scheduleTime', 'scheduleFrequency', 'weekdays'] as const).some(
        key => scheduleHasChanged[key]
      );
      const rrule = hasRruleChanges ? buildRRuleSet(values).toString() : occurrence.rrule;

      const updateInput: UpdateScheduleInput = {
        id: scheduleId,
        name: scheduleName,
        mapId,
        rrule,
        suction: suctionMode,
        cleaningZones,
      };

      dispatch(RobotScheduleActions.updateScheduleRequest(updateInput));
    },
    [robot, scheduleMap, scheduleName, dispatch, buildRRuleSet, isEventOpen, selectedEvent, scheduleHasChanged]
  );

  useEffect(() => {
    // Reset the form's map selection per robot; the shared `maps` slice itself stays untouched since
    // it also backs the "has this robot got a map assigned" routing check elsewhere.
    setScheduleMap(undefined);
    form.setFieldsValue({ scheduleMap: undefined });
    dispatch(
      RobotScheduleActions.listMapsRequest({
        mnsns: [{ materialNumber: robot.materialNumber, serialNumber: robot.serialNumber }],
        filter: {
          limit: 10000,
        },
      })
    );
  }, [dispatch, form, robot.materialNumber, robot.serialNumber]);

  useEffect(() => {
    if (isOpen) {
      const defaultScheduleTime = selectedCreateDateTime ?? dayjs().add(1, 'hour').startOf('hour');
      resetFormState();

      // Preselect the picked slot's weekday. ALL_WEEKDAYS is Monday-indexed; dayjs is Sunday-indexed.
      const slotWeekdays: WeekdayStr[] = selectedCreateDateTime
        ? [ALL_WEEKDAYS[(selectedCreateDateTime.day() + 6) % 7]]
        : [];

      form.setFieldsValue({
        scheduleName: undefined,
        scheduleDate: dayjs(),
        scheduleTime: defaultScheduleTime,
        scheduleMap: undefined,
        scheduleFrequency: ONCE_FREQUENCY,
        suctionMode: CleaningSuctionEnum.Normal,
        cleaningMode: CleaningModeEnum.WholeMap,
        cleaningZones: [],
        weekdays: slotWeekdays,
      });
    }
  }, [form, isOpen, selectedCreateDateTime, resetFormState]);

  useEffect(() => {
    if (isEventOpen && selectedEvent) {
      const occurrence = selectedEvent.resource.scheduleEvent.occurrence;
      const { until, byweekday: byweekdayUtc, freq, dtstart: dtstartLocal } = parseRRuleString(occurrence.rrule);

      eventDtStartLocalRef.current = dayjs(dtstartLocal);

      const eventScheduleFrequency: CV50_FREQUENCY = !until ? freq : ONCE_FREQUENCY;

      const eventScheduleName = selectedEvent.resource.scheduleEvent.name;
      const { startDate, suction, map, cleaningZones: occurrenceCleaningZones, cleaningMode } = occurrence;
      const eventSuctionMode = suction ?? CleaningSuctionEnum.Normal;
      const eventCleaningZones = occurrenceCleaningZones ?? [];
      // Fall back to deriving from cleaningZones for older occurrences the backend hasn't
      // backfilled cleaningMode on yet.
      const eventCleaningMode =
        cleaningMode ?? (eventCleaningZones.length > 0 ? CleaningModeEnum.Area : CleaningModeEnum.WholeMap);
      const mapId = map?.id;
      const eventStartDate = dayjs(startDate);
      // BYDAY was stored in UTC; convert back to local weekdays for the form
      const eventLocalWeekdays = fromRruleWeekdaysUtc(
        (byweekdayUtc ?? []).map(utcWeekday => ALL_WEEKDAYS[utcWeekday]),
        eventStartDate
      );

      const formValues: ScheduleUpsertModalCV50FormValues = {
        scheduleName: eventScheduleName,
        scheduleDate: eventStartDate,
        scheduleTime: eventStartDate,
        scheduleMap: mapId ?? '',
        suctionMode: eventSuctionMode,
        cleaningMode: eventCleaningMode,
        cleaningZones: eventCleaningZones,
        scheduleFrequency: eventScheduleFrequency,
        weekdays: eventLocalWeekdays,
      };

      form.setFieldsValue(formValues);

      editFormValuesRef.current = formValues;
    } else {
      editFormValuesRef.current = null;
      setHasScheduleChanges(false);
      setScheduleHasChanged({});
    }
    // scheduleMaps excluded — resolved in the effect below to avoid clobbering in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEventOpen, selectedEvent, robot]);

  // Resolves the selected map reactively, kept out of the effect above (see its comment).
  useEffect(() => {
    if (!isEventOpen || !selectedEvent) {
      return;
    }

    const mapId = selectedEvent.resource.scheduleEvent.occurrence.map?.id;

    setScheduleMap(mapId ? getSelectedMapById(mapId, scheduleMaps) : undefined);
  }, [isEventOpen, selectedEvent, scheduleMaps]);

  useEffect(() => {
    // Skip while the maps request is in flight so a stale, previous-robot options list can't be
    // auto-selected before the current robot's own map list has loaded.
    if (isOpen && !isEventOpen && !isScheduleMapsLoading && scheduleMapOptions.length === 1) {
      form.setFieldsValue({ scheduleMap: scheduleMapOptions[0].value });
      handleScheduleMapChange({ type: 'map', map: scheduleMapOptions[0].map });
    }
  }, [form, isOpen, isEventOpen, isScheduleMapsLoading, scheduleMapOptions, handleScheduleMapChange]);

  useEffect(() => {
    setHasScheduleChanges(Object.values(scheduleHasChanged).some(value => value));
  }, [scheduleHasChanged]);

  useEffect(() => {
    if (createScheduleData) {
      handleCancel();
      dispatch(RobotScheduleActions.createScheduleReset());
    }
  }, [createScheduleData, handleCancel, dispatch]);

  return {
    form,
    robot,
    scheduleName,
    scheduleMap,
    scheduleMapGroups,
    isScheduleMapsLoading,
    expectedCompletionTime,
    hasScheduleChanges,
    isDropdownVisible,
    hasErrorScheduleName,
    hasErrorScheduleMap,
    isEditingScheduleName,
    isScheduleNameInvalid,
    handleToggleEditingScheduleName,
    handleEditScheduleMap,
    handleDropdownVisibleChange,
    handleScheduleMapChange,
    handleFrequencyChange,
    handleWeekdaysChange,
    handleCleaningZonesChange,
    handleFormValuesChange,
    handleValidateFields,
    handleSaveChanges,
    handleSubmit,
    handleCancel,
  };
};
